/**
 * /admin/spend — AWS Cost Explorer rollup for the ZCD account.
 *
 * Replaces the standalone zen-cost-monitor Flask dashboard for daily
 * "how much did we burn today" checks. Shows this month and last month
 * (each split pre-tax / tax), every service with its usage-type lines
 * (what was billed, how much of it, and last month's figure alongside),
 * and the last 7 days split by service.
 *
 * Source: GET /api/admin/v2/finances/aws-spend/ (10-min server-side
 * cache; three Cost Explorer calls at $0.01 each).
 *
 * Caveats surfaced inline on the page:
 * - Yesterday's daily figure typically lags ~24h (CE backfills).
 * - ZenSportsLive lives in a separate AWS account and is NOT included.
 * - The account also hosts two non-cricket projects; AWS cannot split
 *   them (no cost-allocation tags), so every figure is the whole account.
 *
 * Below the spend cards, <AwsInventory /> lists what the account runs.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, AlertTriangle, Loader2, Cloud, ChevronRight, ChevronDown,
} from "lucide-react";
import { getAwsSpend } from "../../api/owner.api";
import AwsInventory from "./AwsInventory";

const TAX = "Tax";
// Fixed categorical order — a service keeps its colour while it stays in
// the top six; everything else folds into "Other". Never cycled.
const SERIES = [
  "var(--series-1)", "var(--series-2)", "var(--series-3)",
  "var(--series-4)", "var(--series-5)", "var(--series-6)",
];
const OTHER = "Other";

function fmtUsd(amount, digits = 2) {
  if (amount == null) return "—";
  const n = Number(amount);
  if (n === 0) return "$0.00";
  if (Math.abs(n) < 0.01) return "<$0.01";
  return `$${n.toFixed(digits)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-IN", {
    day: "2-digit", month: "short",
  });
}

function fmtQty(q, unit) {
  if (!q) return "";
  const n = Number(q);
  const num = n >= 1e6 ? `${(n / 1e6).toFixed(2)}M`
    : n >= 1e4 ? `${(n / 1e3).toFixed(1)}k`
    : n >= 100 ? n.toFixed(0)
    : n.toFixed(2);
  return `${num} ${unit || ""}`.trim();
}

// Short service names for the chart legend; the table keeps AWS's names.
const SHORT = {
  "AWS Elemental MediaConvert": "MediaConvert",
  "Amazon Relational Database Service": "Database (RDS)",
  "Amazon Elastic Compute Cloud - Compute": "EC2 servers",
  "EC2 - Other": "EC2 disks & snapshots",
  "Amazon Simple Storage Service": "S3 storage",
  "Amazon Virtual Private Cloud": "Public IPs (VPC)",
  "AWS Lambda": "Lambda",
  "AWS Cost Explorer": "Cost Explorer",
  "AmazonCloudWatch": "CloudWatch",
  "Amazon CloudFront": "CloudFront",
};
const short = (s) => SHORT[s] || s;

export default function AdminSpend() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [showFree, setShowFree] = useState(false);
  const [hoverDay, setHoverDay] = useState(null);
  const [allLines, setAllLines] = useState(() => new Set());

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await getAwsSpend();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  const services = data?.mtd_by_service || [];
  const billed = services.filter((s) => s.amount_usd > 0 || s.prev_month_usd > 0);
  const free = services.filter((s) => !(s.amount_usd > 0 || s.prev_month_usd > 0));
  const shown = showFree ? services : billed;

  // Chart series: the six biggest services over the 7 days (tax excluded —
  // it tracks the others), the rest as "Other".
  const chart = useMemo(() => {
    const days = data?.daily_last_7d || [];
    const totals = {};
    days.forEach((d) => Object.entries(d.by_service || {}).forEach(([s, a]) => {
      if (s !== TAX) totals[s] = (totals[s] || 0) + a;
    }));
    const top = Object.entries(totals).sort((a, b) => b[1] - a[1])
      .slice(0, SERIES.length).map(([s]) => s);
    const colour = Object.fromEntries(top.map((s, i) => [s, SERIES[i]]));
    const rows = days.map((d) => {
      const segs = top.map((s) => ({ s, a: d.by_service?.[s] || 0 }));
      const other = Object.entries(d.by_service || {})
        .filter(([s]) => s !== TAX && !colour[s])
        .reduce((t, [, a]) => t + a, 0);
      if (other > 0) segs.push({ s: OTHER, a: other });
      return { ...d, segs, tax: d.by_service?.[TAX] || 0 };
    });
    const max = Math.max(1, ...rows.map((r) => r.segs.reduce((t, x) => t + x.a, 0)));
    const hasOther = rows.some((r) => r.segs.some((x) => x.s === OTHER));
    return { rows, top, colour, max, hasOther };
  }, [data]);

  const toggle = (svc) => setOpen((prev) => {
    const next = new Set(prev);
    next.has(svc) ? next.delete(svc) : next.add(svc);
    return next;
  });

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Cloud className="w-5 h-5" /> AWS Spend
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Whole AWS account 890989339178, including the two non-cricket
            projects that share it (AWS cannot split them). ZenSportsLive
            runs in a separate account and is not included.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.cache_age_sec != null && (
            <span className="text-[11px] text-text-secondary">
              cache age: {data.cache_age_sec}s
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-border-default hover:bg-bg-secondary disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
          <div>{error}</div>
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KpiCard
              label="Month-to-date"
              value={fmtUsd(data.mtd_total_usd)}
              sub={data.mtd_tax_usd != null
                ? `${fmtUsd(data.mtd_pretax_usd)} + ${fmtUsd(data.mtd_tax_usd)} tax · since ${fmtDate(data.month_start)}`
                : `since ${fmtDate(data.month_start)}`}
            />
            <KpiCard
              label="Previous month"
              value={fmtUsd(data.previous_month_total_usd)}
              sub={data.previous_month_tax_usd != null
                ? `${fmtUsd(data.previous_month_pretax_usd)} + ${fmtUsd(data.previous_month_tax_usd)} tax`
                : null}
            />
            <KpiCard
              label="MTD vs prev month"
              value={
                data.previous_month_total_usd
                  ? `${Math.round(
                      (data.mtd_total_usd / data.previous_month_total_usd) * 100,
                    )}%`
                  : "—"
              }
              sub="run-rate share so far"
            />
          </div>

          {/* By service, with usage lines */}
          <section className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
            <header className="px-4 py-2.5 border-b border-border-default text-sm font-medium flex items-center justify-between gap-3 flex-wrap">
              <span>
                By service
                <span className="ml-2 text-[11px] text-text-secondary font-normal">
                  click a service to see what it billed for
                </span>
              </span>
              {free.length > 0 && (
                <label className="flex items-center gap-1.5 text-[12px] text-text-secondary font-normal cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showFree}
                    onChange={(e) => setShowFree(e.target.checked)}
                  />
                  show {free.length} services used at no charge
                </label>
              )}
            </header>
            {shown.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Service</th>
                      <th className="text-right px-4 py-2 font-medium w-28">This month</th>
                      <th className="text-right px-4 py-2 font-medium w-28">Last month</th>
                      <th className="text-right px-4 py-2 font-medium w-24">% of month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((row) => {
                      const isOpen = open.has(row.service);
                      const pct = data.mtd_total_usd
                        ? (row.amount_usd / data.mtd_total_usd) * 100
                        : 0;
                      const lines = row.usage || [];
                      // Lines that cost nothing in either month fold away
                      // unless asked for; a no-charge service shows them all.
                      const charged = (u) => u.amount_usd >= 0.005 || (u.prev_month_usd || 0) >= 0.005;
                      const serviceCharged = row.amount_usd >= 0.005 || (row.prev_month_usd || 0) >= 0.005;
                      const quiet = serviceCharged ? lines.filter((u) => !charged(u)) : [];
                      const visible = allLines.has(row.service) || !serviceCharged
                        ? lines : lines.filter(charged);
                      return (
                        <Fragment key={row.service}>
                          <tr
                            className={`border-t border-border-default ${lines.length ? "cursor-pointer hover:bg-bg-secondary/40" : ""}`}
                            onClick={() => lines.length && toggle(row.service)}
                          >
                            <td className="px-4 py-2">
                              <span className="inline-flex items-center gap-1.5">
                                {lines.length
                                  ? (isOpen
                                      ? <ChevronDown className="w-3.5 h-3.5 text-text-secondary" />
                                      : <ChevronRight className="w-3.5 h-3.5 text-text-secondary" />)
                                  : <span className="w-3.5" />}
                                {row.service === TAX ? "Tax (GST on AWS charges)" : row.service}
                                {row.amount_usd === 0 && lines.some((u) => u.quantity > 0) && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-secondary text-text-secondary">
                                    no charge
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {fmtUsd(row.amount_usd)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-text-secondary">
                              {row.prev_month_usd != null ? fmtUsd(row.prev_month_usd) : "—"}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-text-secondary">
                              {pct.toFixed(1)}%
                            </td>
                          </tr>
                          {isOpen && visible.map((u) => (
                            <tr key={u.usage_type} className="bg-bg-secondary/20 text-[13px]">
                              <td className="pl-11 pr-4 py-1.5">
                                <div>{row.service === TAX ? `on ${u.label}` : u.label}</div>
                                <div className="text-[11px] text-text-secondary">
                                  {fmtQty(u.quantity, u.unit)}
                                  {u.label !== u.usage_type && (
                                    <span className="ml-1 opacity-70">· {u.usage_type}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-1.5 text-right tabular-nums align-top">
                                {fmtUsd(u.amount_usd, u.amount_usd < 1 ? 4 : 2)}
                              </td>
                              <td className="px-4 py-1.5 text-right tabular-nums text-text-secondary align-top">
                                {u.prev_month_usd != null ? fmtUsd(u.prev_month_usd, u.prev_month_usd < 1 ? 4 : 2) : "—"}
                              </td>
                              <td />
                            </tr>
                          ))}
                          {isOpen && quiet.length > 0 && (
                            <tr className="bg-bg-secondary/20 text-[12px]">
                              <td colSpan={4} className="pl-11 pr-4 py-1.5">
                                <button
                                  type="button"
                                  className="text-text-secondary underline underline-offset-2 hover:text-text-primary"
                                  onClick={() => setAllLines((prev) => {
                                    const next = new Set(prev);
                                    next.has(row.service) ? next.delete(row.service) : next.add(row.service);
                                    return next;
                                  })}
                                >
                                  {allLines.has(row.service)
                                    ? "hide lines with no charge"
                                    : `show ${quiet.length} more lines with no charge`}
                                </button>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-border-default font-medium">
                      <td className="px-4 py-2">Total</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtUsd(data.mtd_total_usd)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-text-secondary">
                        {fmtUsd(data.previous_month_total_usd)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-text-secondary">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="px-4 py-6 text-sm text-text-secondary text-center">
                No spend recorded this month yet.
              </div>
            )}
          </section>

          {/* Daily 7d, stacked by service */}
          <section className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
            <header className="px-4 py-2.5 border-b border-border-default text-sm font-medium">
              Last 7 days by service
              <span className="ml-2 text-[11px] text-text-secondary font-normal">
                most recent day usually lags ~24h while CE backfills · totals include tax
              </span>
            </header>
            <div className="p-3 space-y-2">
              {chart.top.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-text-secondary pb-1">
                  {chart.top.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: chart.colour[s] }} />
                      {short(s)}
                    </span>
                  ))}
                  {chart.hasOther && (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--series-other)" }} />
                      Other services
                    </span>
                  )}
                </div>
              )}
              {chart.rows.length
                ? chart.rows.map((row) => (
                    <div
                      key={row.date}
                      className="flex items-center gap-3 text-sm rounded px-1 -mx-1 hover:bg-bg-secondary/40"
                      onMouseEnter={() => setHoverDay(row.date)}
                      onMouseLeave={() => setHoverDay(null)}
                      onClick={() => setHoverDay(hoverDay === row.date ? null : row.date)}
                    >
                      <div className="w-16 text-text-secondary tabular-nums">
                        {fmtDate(row.date)}
                      </div>
                      <div className="flex-1 h-3 flex gap-[2px]">
                        {row.segs.filter((x) => x.a > 0).map((x) => (
                          <div
                            key={x.s}
                            className="h-3 first:rounded-l last:rounded-r"
                            title={`${short(x.s)}: ${fmtUsd(x.a)}`}
                            style={{
                              width: `${(x.a / chart.max) * 100}%`,
                              background: x.s === OTHER ? "var(--series-other)" : chart.colour[x.s],
                            }}
                          />
                        ))}
                      </div>
                      <div className="w-20 text-right tabular-nums">
                        {fmtUsd(row.amount_usd)}
                      </div>
                    </div>
                  ))
                : (
                  <div className="text-sm text-text-secondary px-1 py-3">
                    No daily data.
                  </div>
                )}
              {hoverDay && (() => {
                const row = chart.rows.find((r) => r.date === hoverDay);
                if (!row) return null;
                const items = Object.entries(row.by_service || {}).sort((a, b) => b[1] - a[1]);
                return (
                  <div className="mt-2 rounded-md border border-border-default bg-bg-secondary/40 p-2.5 text-[12px]">
                    <div className="font-medium mb-1">{fmtDate(row.date)} · {fmtUsd(row.amount_usd)}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
                      {items.map(([s, a]) => (
                        <div key={s} className="flex items-center justify-between gap-3">
                          <span className="inline-flex items-center gap-1.5 text-text-secondary">
                            <span
                              className="w-2 h-2 rounded-sm"
                              style={{ background: s === TAX ? "transparent" : (chart.colour[s] || "var(--series-other)") }}
                            />
                            {s === TAX ? "Tax" : short(s)}
                          </span>
                          <span className="tabular-nums">{fmtUsd(a)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </section>
        </>
      )}

      <AwsInventory />
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary p-4">
      <div className="text-[11px] uppercase tracking-wider text-text-secondary">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-text-secondary">{sub}</div>}
    </div>
  );
}
